import * as React from "react";
import { Button as MuiButton, ButtonProps as MuiButtonProps, styled, colors } from "@mui/material";

interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  variant?: "contained" | "outlined" | "text" | "glass";
  children: React.ReactNode;
  glassColor?: "default" | "green";
}

const StyledGlassButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== "glassColor",
})<{ glassColor?: "default" | "green"; }>(({ theme, glassColor }) => {
  const isDark = theme.palette.mode === "dark";

  if (glassColor === "green") {
    return {
      background: `linear-gradient(135deg, ${colors.green[400]}80 0%, ${colors.green[300]}60 100%)`,
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      border: `1px solid ${colors.green[300]}80`,
      borderRadius: theme.shape.borderRadius * 1.5,
      color: "#fff",
      fontWeight: 600,
      boxShadow: "0 4px 16px 0 rgba(76, 175, 80, 0.3)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        background: `linear-gradient(135deg, ${colors.green[500]}90 0%, ${colors.green[400]}70 100%)`,
        transform: "translateY(-2px)",
        boxShadow: "0 6px 20px 0 rgba(76, 175, 80, 0.4)",
        border: `1px solid ${colors.green[400]}90`,
      },
      "&:active": {
        transform: "translateY(0)",
      },
      "&:disabled": {
        background: `linear-gradient(135deg, ${colors.green[200]}40 0%, ${colors.green[100]}30 100%)`,
        border: `1px solid ${colors.green[200]}40`,
        color: colors.green[300],
      },
    };
  }

  return {
    background: isDark
      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)`
      : `linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: theme.shape.borderRadius * 1.5,
    color: theme.palette.text.primary,
    boxShadow: "0 4px 16px 0 rgba(31, 38, 135, 0.2)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: isDark
        ? `linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)`
        : `linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.15) 100%)`,
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px 0 rgba(31, 38, 135, 0.3)",
      border: "1px solid rgba(255, 255, 255, 0.4)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  };
});

const Button: React.FC<ButtonProps> = ({ variant = "contained", glassColor, ...props }) => {
  if (variant === "glass") {
    return <StyledGlassButton glassColor={glassColor} {...props} variant="text" />;
  }

  return <MuiButton variant={variant} {...props} />;
};

export default Button;

